// app/shared/utils/api-helpers.ts
/**
 * Global API utility functions to replace all direct .json() calls
 */

import { serverHealthMonitor } from "./server-health";

export interface ApiResponse<T = any> {
  data?: T;
  error?: string;
  success?: boolean;
}

/**
 * Turn whatever an error body carries into a sentence a person can read.
 *
 * BFF routes forward FastAPI's `detail` verbatim, and on a 422 that is an
 * array of `{loc, msg, type}` objects, not a string. Handing it to
 * `new Error(...)` is what rendered "[object Object]" in the campaign wizard.
 */
function readableApiError(value: unknown): string | undefined {
  if (typeof value === "string") return value.trim() || undefined;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  if (Array.isArray(value)) {
    const parts = value.map(readableApiError).filter(Boolean) as string[];
    return parts.length ? parts.join("; ") : undefined;
  }
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    // FastAPI validation entry: name the offending field, then the reason.
    if (typeof record.msg === "string") {
      const location = Array.isArray(record.loc)
        ? record.loc
            .filter((part) => part !== "body" && part !== "query")
            .join(".")
        : "";
      return location ? `${location}: ${record.msg}` : record.msg;
    }
    return (
      readableApiError(record.detail) ??
      readableApiError(record.error) ??
      readableApiError(record.message)
    );
  }
  return undefined;
}

/**
 * Enhanced fetch wrapper that handles all the edge cases
 */
export async function apiRequest<T = any>(
  url: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      ...options,
    });

    // Get response text first
    const text = await response.text();
    // Handle empty or invalid responses
    if (
      !text ||
      text.trim() === "" ||
      text === "undefined" ||
      text === "null"
    ) {
      if (response.ok) {
        // Mark server as healthy on successful response
        serverHealthMonitor.markHealthy();
        return { success: true, data: null as T };
      } else {
        return {
          success: false,
          error: `HTTP ${response.status}: Empty response`,
        };
      }
    }

    // Parse JSON safely
    let data: any;
    try {
      data = JSON.parse(text);
    } catch (parseError) {
      console.error(`[apiRequest] JSON parse error for ${url}:`, parseError);
      return {
        success: false,
        error: `Invalid JSON response: ${text.substring(0, 100)}...`,
      };
    }

    // Handle HTTP errors
    if (!response.ok) {
      return {
        success: false,
        error: readableApiError(data) || `HTTP ${response.status}`,
        data: data as T,
      };
    }

    // Mark server as healthy on successful response
    serverHealthMonitor.markHealthy();

    return {
      success: true,
      data: data as T,
    };
  } catch (error) {
    console.error(`[apiRequest] Network error for ${url}:`, error);

    // Mark server as unhealthy on network errors
    const errorMsg = (error as Error)?.message?.toLowerCase() || "";
    const isNetworkError =
      errorMsg.includes("failed to fetch") ||
      errorMsg.includes("network") ||
      errorMsg.includes("econnrefused") ||
      errorMsg.includes("connection refused");

    if (isNetworkError) {
      serverHealthMonitor.markUnhealthy();
    }

    return {
      success: false,
      error: error instanceof Error ? error.message : "Network error",
    };
  }
}

/**
 * GET request helper
 */
export async function apiGet<T = any>(url: string): Promise<ApiResponse<T>> {
  return apiRequest<T>(url, { method: "GET" });
}

/**
 * POST request helper
 */
export async function apiPost<T = any>(
  url: string,
  body?: any,
): Promise<ApiResponse<T>> {
  return apiRequest<T>(url, {
    method: "POST",
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * PUT request helper
 */
export async function apiPut<T = any>(
  url: string,
  body?: any,
): Promise<ApiResponse<T>> {
  return apiRequest<T>(url, {
    method: "PUT",
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * PATCH request helper
 */
export async function apiPatch<T = any>(
  url: string,
  body?: any,
): Promise<ApiResponse<T>> {
  return apiRequest<T>(url, {
    method: "PATCH",
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * DELETE request helper
 */
export async function apiDelete<T = any>(url: string): Promise<ApiResponse<T>> {
  return apiRequest<T>(url, { method: "DELETE" });
}
